import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useRouteMatch } from 'react-router-dom';
import { toast } from 'sonner';
import { TemplateType } from '../../../extViewmodels';
import { DefaultSupportedIndustries, ILocationState } from '../../../models';
import { Topics } from '../../../models/LocalNotificationTopics';
import { useLocalNotificationService } from '../../../models/LocalNotifications';
import { useErrorMessages, useFullscreenModal } from '../../../models/hooks/appStateHooks';
import { CreateGhostCalendarSuggestionViewModel } from '../../../viewmodels/AdminViewModels';
import {
	ConfirmationDialog,
	DefaultDeleteConfirmationOptions,
	IConfirmationDialogOption,
} from '../../../web/components/ConfirmationDialog';
import { WarningIcon } from '../../../web/components/svgs/icons/WarningIcon';
import { EditGhostCalendarSuggestionStep } from '../../containers/templates/EditGhostCalendarSuggestion/models';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	createSuggestion: CreateGhostCalendarSuggestionViewModel;
	styles?: StyleDeclarationValue[];
}

interface ICalendarCardProps {
	buttonText: string;
	title: string;
	text: string;
	onClick: () => void;
}

export const GhostCalendarSummary: React.FC<IProps> = observer(props => {
	const { className, createSuggestion, styles = [] } = props;
	const { postNotification } = useLocalNotificationService();
	const fullscreenModal = useFullscreenModal();
	const match = useRouteMatch();
	const errorMessages = useErrorMessages();
	const isEmailSuggestion = createSuggestion.template?.templateType === TemplateType.Email;
	const isSocialSuggestion = createSuggestion.template?.templateType === TemplateType.SocialMediaPost;

	// #region delete confirmation
	const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState<boolean>(false);
	const deleteSuggestion = React.useCallback(() => {
		createSuggestion
			?.delete()
			.then(() => {
				fullscreenModal.dismissModal();

				toast.success('Suggestion deleted');
				postNotification({ topic: Topics.GHOST_CAL_UPDATE });
			})
			.catch(err => {
				errorMessages.pushApiError(err);
			});
	}, [createSuggestion, errorMessages, fullscreenModal, postNotification]);

	const onDeleteConfirmationRequestClose = React.useCallback(
		(result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
			setShowDeleteConfirmation(false);
			if (result?.isDestructive && !cancel) {
				deleteSuggestion();
			}
		},
		[deleteSuggestion]
	);

	const onRequestDelete = React.useCallback(() => {
		setShowDeleteConfirmation(true);
	}, []);
	// #endregion

	const onEdit = (route: string) => () => {
		const locationState: ILocationState<CreateGhostCalendarSuggestionViewModel, any> = { viewModel: createSuggestion };
		fullscreenModal?.history.push({
			pathname: `${match.url.replace('/summary', `/${route}`)}`,
			state: locationState,
		});
	};

	const renderVerticalsInfo = () => {
		if (createSuggestion?.industries?.length === 1) {
			return createSuggestion?.industries[0];
		}
		return createSuggestion?.industries?.length === DefaultSupportedIndustries.length
			? 'All industries'
			: `${createSuggestion?.industries?.length} industries`;
	};
	const renderSubverticalsInfo = () => {
		if (createSuggestion?.subverticals?.length === 1) {
			return createSuggestion?.subverticals[0];
		}
		return createSuggestion?.subverticals?.length === 0
			? 'All subverticals'
			: `${createSuggestion?.subverticals?.length} subverticals`;
	};

	const CalendarCard = ({ buttonText, title, text, onClick }: ICalendarCardProps) => {
		return (
			<article className={css(styleSheet.card)}>
				<h3 className={css(styleSheet.title)}>{title}</h3>
				<div className={css(styleSheet.text)}>
					<p>{text}</p>
					<button className={css(styleSheet.edit)} onClick={onClick}>
						{buttonText}
					</button>
				</div>
			</article>
		);
	};
	return (
		<div className={`${css(styleSheet.container, ...styles)} ghost-calendar-summary ${className || ''}`}>
			<div className={`${css(styleSheet.container, ...styles)} ghost-calendar-summary ${className || ''}`}>
				{isEmailSuggestion ? (
					<article className={css(styleSheet.card)}>
						<h3 className={css(styleSheet.title)}>Send From</h3>
						<span className={css(styleSheet.text)}>Contact Owners</span>
					</article>
				) : null}
				<CalendarCard
					buttonText='Edit'
					title='Verticals / Subverticals'
					text={`${renderVerticalsInfo()}: ${renderSubverticalsInfo()}`}
					onClick={onEdit(EditGhostCalendarSuggestionStep.Subverticals)}
				/>
				<CalendarCard
					buttonText='Edit'
					title='Levitate Customers'
					text={createSuggestion?.accountAges?.length === 2 ? 'All customers' : createSuggestion?.accountAges[0]}
					onClick={onEdit(EditGhostCalendarSuggestionStep.Subverticals)}
				/>
				{isSocialSuggestion && createSuggestion?.targets?.length > 0 ? (
					<article className={css(styleSheet.card)}>
						<h3 className={css(styleSheet.title)}>Social Media Platforms</h3>
						<div className={css(styleSheet.text)}>
							<p>
								{createSuggestion?.targets
									.slice()
									.sort((a, b) => (a.provider > b.provider ? 1 : -1))
									.map(target => target.provider.toString())
									.join(', ')}
							</p>
							<button className={css(styleSheet.edit)} onClick={onEdit(EditGhostCalendarSuggestionStep.Targets)}>
								Edit
							</button>
						</div>
					</article>
				) : null}
				{isEmailSuggestion ? (
					<CalendarCard
						buttonText='Edit'
						title='Recipient Tags'
						text={createSuggestion?.tags?.length >= 1 ? `${createSuggestion?.tags?.length} tags` : 'None selected'}
						onClick={onEdit(EditGhostCalendarSuggestionStep.Tags)}
					/>
				) : null}
				<CalendarCard
					buttonText='Edit'
					title={
						[TemplateType.SocialMediaPost, TemplateType.Blog].includes(createSuggestion?.template?.templateType)
							? 'Post Date'
							: 'Send Date'
					}
					text={createSuggestion?.startDate?.format('MMM DD, YYYY')}
					onClick={onEdit(EditGhostCalendarSuggestionStep.SendOptions)}
				/>
			</div>
			<button className={css(styleSheet.delete)} onClick={onRequestDelete}>
				Delete this {createSuggestion?.template?.templateType === TemplateType.Blog ? 'Post' : 'Campaign'} from Ghost
				Calendar
			</button>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: !!showDeleteConfirmation,
					onRequestClose: onDeleteConfirmationRequestClose,
				}}
				options={DefaultDeleteConfirmationOptions}
				title={`Are you sure you want to delete this suggested ${createSuggestion?.template?.templateType === TemplateType.Blog ? 'Post' : 'Campaign'}?"`}
			/>
		</div>
	);
});
